/* eslint-disable @next/next/no-img-element */
'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Video } from "@/components/fun-ui/Video";

type WorkflowFeature = {
  resource: string;
  caption: string;
};

type WorkflowFeaturesProps = {
  features?: WorkflowFeature[];
}

const defaultFeatures = [{
  "resource": "https://oss.findinai.cn/asset/20250902/20250902-223200",
  "caption": "文献综述",
}, {
  "resource": "https://oss.findinai.cn/video/20240428/new-section-vedio-literature-selection",
  "caption": "文献筛选",
}, {
  "resource": "https://oss.findinai.cn/video/20240428/new-section-vedio-literature-close-reading",
  "caption": "文献精读",
}, {
  "resource": "https://oss.findinai.cn/video/20250715/new-section-vedio-paper-translation",
  "caption": "文献翻译",
},
{
  "resource": "https://oss.findinai.cn/video/20240428/new-section-vedio-note-excerpt",
  "caption": "笔记摘录",
}, {
  "resource": "https://oss.findinai.cn/video/20240428/new-section-vedio-theme-study",
  "caption": "主题分类",
}, {
  "resource": "https://oss.findinai.cn/video/20240428/new-section-vedio-academic-writing",
  "caption": "学术写作",
}];

export const WorkflowFeatures = ({
  features = defaultFeatures,
}: WorkflowFeaturesProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const elemRef = useRef<HTMLDivElement | null>(null);
  const [featureElems, setFeatureElems] = useState<HTMLDivElement[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>(Array(features.length).fill(null));

  useEffect(() => {
    const el = elemRef.current;
    if (el) {
      const els = Array.from(el.querySelectorAll('[data-feature-index]'));
      setFeatureElems(els.map(el => (el as HTMLDivElement)));
    }
  }, []);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (index !== currentIndex && video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    if (videoRefs.current[currentIndex]) {
      videoRefs.current[currentIndex]!.currentTime = 0;
      videoRefs.current[currentIndex]!.play();
    }
  }, [currentIndex]);

  const pointerElemStyle = useMemo(() => {
    const featureElem = featureElems[currentIndex];
    if (featureElem) {
      const w = featureElem.offsetWidth;
      const l = featureElem.offsetLeft;
      return {
        width: `${w / 2}px`,
        left: `${l + w / 4}px`,
        marginLeft: 0,
      }
    }
    return {};
  }, [currentIndex, featureElems]);

  const handleVideoEnd = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % features.length);
  }, [features.length]);

  const handleFeatureClick = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="h-full flex flex-col items-center justify-center -mx-[42px] overflow-hidden">
      <div
        ref={elemRef}
        className="flex relative space-x-10 text-lg text-foreground mt-9"
      >
        {
          features.map((feature, index) => (
            <div
              key={index}
              data-feature-index={index}
              className={[
                "text-xl pb-4",
                currentIndex === index && "text-primary",
              ].join(' ')}
              onClick={() => {
                handleFeatureClick(index);
              }}
            >
              <span className="cursor-pointer">{feature.caption}</span>
            </div>
          ))
        }
        <div
          className="absolute bg-primary left-0 bottom-0 h-1 w-10 ml-0 transition-all duration-300"
          style={pointerElemStyle}
        ></div>
      </div>
      <div className="h-full flex transition-transform duration-300 mt-14 mb-[114px]" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {
          features.map((feature, index) => (
            <div key={index} className="h-full w-full px-[42px] flex-shrink-0 items-center justify-center">
              <div className="rounded-[20px] shadow-[0_9px_28px_0_rgba(78,89,105,0.1)]">
                <Video
                  ref={el => { videoRefs.current[index] = el; }}
                  className="rounded-[20px]"
                  mp4Src={`${feature.resource}.mp4?_t=1752854774571`}
                  webmSrc={`${feature.resource}.webm?_t=1752854774571`}
                  loop={false}
                  onEnded={handleVideoEnd}
                />
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};
